import React from "react";
import { widgetRefOf } from "../../../helpers/widgetRef";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import LightSelect from "../../CompanyView/LightSelect";
import InfoTip from "./InfoTip";
import { UseCompanyWorkspaces } from "../../../hooks/company";
import {
  DEFAULT_LINES,
  EXPENSE_UNITS,
  GRAINS,
  emptyExpense,
  expensesOf,
  findTotalRevenueKpi,
  goalOf,
  laborBaseOf,
  laborModeOf,
  payrollWidgetsOn,
  revenueModeOf,
  revenueOf,
  SERIES_KEYS,
} from "../../../components/ProfitTrend/data";
import "./customize.css";

/**
 * Setup for Revenue, Expenses & Labor.
 *
 * Four questions in the order they matter: what the company made, what payroll
 * cost, what everything else cost, and where the lines are that must not be
 * crossed. Everything else on this panel is how it's drawn.
 *
 * Expenses used to split into payroll vs non-payroll (a payroll cost fed
 * Labor instead of Expenses, so wages weren't counted twice). Kelvin asked to
 * drop that distinction from the UI for now — every typed expense is a plain
 * "opex" row and lands on the Expenses line. `data.js` still understands a
 * "payroll" kind (old saved rows keep working), there is just no way to
 * create one here any more.
 */

const SERIES_LABEL = {
  revenue: "Revenue",
  expenses: "Expenses",
  labor: "Labor",
  net: "Net profit",
};

export const Pick = ({ value, options, onChange, placeholder }) => (
  <LightSelect
    width="100%"
    height={36}
    fontSize={12.5}
    radius={8}
    menuWidth={300}
    placeholder={placeholder}
    value={value || ""}
    options={options}
    onChange={(o) => onChange(o.value)}
  />
);
Pick.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
Pick.defaultProps = { value: "", placeholder: "Choose" };

const Num = ({ label, value, onChange, prefix, suffix, placeholder }) => (
  <label className="gmbw-lbr-num">
    <span>{label}</span>
    <span className="gmbw-lbr-numbox">
      {prefix ? <i>{prefix}</i> : null}
      <input
        type="number"
        step="any"
        inputMode="decimal"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix ? <i>{suffix}</i> : null}
    </span>
  </label>
);
Num.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  placeholder: PropTypes.string,
};
Num.defaultProps = { value: "", prefix: null, suffix: null, placeholder: "0" };

const Switch = ({ on, label, note, onToggle }) => (
  <div className="gmbw-toggle-block">
    <div className={`gmbw-toggle${note ? " noted" : ""}`}>
      <span>
        {label}
        {note && <i className="gmbw-toggle-note">{note}</i>}
      </span>
      <div className="gmbw-toggle-ctl">
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={label}
          className={`gmbw-switch${on ? " on" : ""}`}
          onClick={onToggle}
        >
          <span className="gmbw-switch-dot" />
        </button>
      </div>
    </div>
  </div>
);
Switch.propTypes = {
  on: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  note: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
};
Switch.defaultProps = { note: null };

const IcX = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const ProfitTrendSetup = ({ draft, onChange, boardWidgets }) => {
  const workspaces = UseCompanyWorkspaces(true) || [];
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);

  const set = (patch) => onChange({ ...draft, ...patch });
  const revenueMode = revenueModeOf(draft);
  const laborMode = laborModeOf(draft);
  const laborBase = laborBaseOf(draft);
  const labelOf = (id) => (draft?.labels || {})[id] || SERIES_LABEL[id];
  const items = expensesOf(draft);
  const lines = Array.isArray(draft?.lines) ? draft.lines : DEFAULT_LINES;

  /* Only ever READ here, to say whether the metric exists. The card resolves
     "auto" live on every render (see data.js), so Setup deliberately stores
     nothing for it — a self-heal effect writing the same ids back would fire a
     board save on every mount and buy nothing. */
  const autoRevKpi = findTotalRevenueKpi(workspaces, companyKpis);

  /* -- Revenue, when a specific metric is picked by hand ----------------- */
  const pickedWsId = String(draft?.revenueWorkspaceId || "");
  /* The full catalog for the workspace, NOT filterLiveKpis — that caps a
     workspace at five "Live" metrics, and a revenue metric sitting outside
     that five would simply be unpickable here (the same trap Payroll vs
     Revenue's hours picker had to avoid). */
  const kpiOptions = (companyKpis?.[pickedWsId] || [])
    .filter((k) => k && !k.isDraft && !k.disabled)
    .map((k) => ({ value: String(k._id), label: k.name || "Metric" }));

  /* -- Labor -------------------------------------------------------------- */
  const payrollCards = payrollWidgetsOn(boardWidgets);
  const payrollOptions = payrollCards.map((w, i) => ({
    value: widgetRefOf(w),
    label: w.title || `Payroll vs Revenue${payrollCards.length > 1 ? ` (${i + 1})` : ""}`,
  }));

  /* -- Expenses ----------------------------------------------------------- */
  const setItems = (next) => set({ expenses: next });
  const patchItem = (i, patch) => setItems(items.map((e, ix) => (ix === i ? { ...e, ...patch } : e)));

  /* -- Goals -------------------------------------------------------------- */
  const setGoal = (key, patch) => set({
    goals: { ...(draft?.goals || {}), [key]: { ...goalOf(draft, key), ...patch } },
  });

  const toggleLine = (id) => {
    const next = lines.includes(id) ? lines.filter((x) => x !== id) : [...lines, id];
    /* Keep the plot order stable whichever order they were ticked in, so the
       legend doesn't reshuffle every time one is switched back on. */
    set({ lines: ["revenue", "expenses", "labor", "net"].filter((x) => next.includes(x)) });
  };

  return (
    <>
      {/* ── Revenue ───────────────────────────────────────────────────── */}
      <div className="gmbw-fld">
        <label>
          Revenue
          <InfoTip text="The figure every share on this card is measured against. Net profit, Expense % and Labor % are all fractions of it, so this is the one number that has to be right." />
        </label>
        <Pick
          value={revenueMode}
          options={[
            { value: "auto", label: "Your Total Revenue metric" },
            { value: "pick", label: "A metric I choose" },
            { value: "manual", label: "Type it in" },
          ]}
          onChange={(v) => set({ revenueMode: v })}
        />
        {revenueMode === "auto" && (
          <p className="gmbw-hint">
            {autoRevKpi
              ? "Synced from your Total Revenue metric, summed over this card's date range — the same metric Payroll vs Revenue reads, so the two cards can never disagree about what revenue was."
              : "No Total Revenue metric found. Add one (Add Metric → Housecall Pro), pick another metric above, or type the figure in."}
          </p>
        )}
        {revenueMode === "pick" && (
          <>
            <div className="gmbw-lbr-grid" style={{ marginTop: 8 }}>
              <div className="gmbw-lbr-full">
                <Pick
                  value={pickedWsId}
                  placeholder="Which workspace"
                  options={workspaces.map((w) => ({ value: String(w._id), label: w.name }))}
                  onChange={(v) => set({ revenueWorkspaceId: v, revenueKpiId: "" })}
                />
              </div>
              <div className="gmbw-lbr-full">
                <Pick
                  value={String(draft?.revenueKpiId || "")}
                  placeholder={pickedWsId ? "Which metric" : "Pick a workspace first"}
                  options={kpiOptions}
                  onChange={(v) => set({ revenueKpiId: v })}
                />
              </div>
            </div>
            <p className="gmbw-hint">Summed over this card&apos;s date range, the same way the metric widgets on this board sum it.</p>
          </>
        )}
        {revenueMode === "manual" && (
          <>
            <div className="gmbw-lbr-solo">
              <Num
                label="What the company made this period"
                value={draft?.revenue ?? ""}
                prefix="$"
                onChange={(v) => set({ revenue: v })}
              />
            </div>
            <p className="gmbw-hint">
              A typed figure has no daily shape, so the Revenue line runs flat across the range. Point this at a
              metric to get the real curve.
            </p>
          </>
        )}
      </div>

      {/* ── Labor ─────────────────────────────────────────────────────── */}
      <div className="gmbw-fld">
        <label>
          Labor
          <InfoTip text="Wages. Read from a Payroll vs Revenue card on this dashboard so the two cards always agree — it already knows each department's hours and pay rates." />
        </label>
        <Pick
          value={laborMode}
          options={[
            { value: "widget", label: "From a Payroll vs Revenue card" },
            { value: "manual", label: "Type it in" },
          ]}
          onChange={(v) => set({ laborMode: v })}
        />
        {laborMode === "widget" && payrollOptions.length === 0 && (
          <p className="gmbw-hint">
            There is no Payroll vs Revenue card on this dashboard yet. Add one from the component library, or
            switch to &quot;Type it in&quot; above.
          </p>
        )}
        {laborMode === "widget" && payrollOptions.length > 0 && (
          <>
            <div style={{ marginTop: 8 }}>
              <Pick
                value={String(draft?.laborWidgetId || (payrollOptions.length === 1 ? payrollOptions[0].value : ""))}
                placeholder="Which card"
                options={payrollOptions}
                onChange={(v) => set({ laborWidgetId: v })}
              />
            </div>
            <p className="gmbw-hint">
              {payrollOptions.length > 1
                ? "This dashboard has more than one — they track different departments, so pick the one whose payroll belongs on this chart."
                : "Its departments, hours and pay rates, re-read for this card's own date range. Hover a point on the chart to see each department's share."}
            </p>
          </>
        )}
        {laborMode === "manual" && (
          <>
            <div className="gmbw-lbr-grid" style={{ marginTop: 8 }}>
              <Num
                label="Wages for this period"
                value={draft?.laborManual}
                prefix={draft?.laborManualUnit === "%" ? null : "$"}
                suffix={draft?.laborManualUnit === "%" ? "% of rev" : null}
                onChange={(v) => set({ laborManual: v })}
              />
              <div>
                <span className="gmbw-lbr-numlabel">Entered as</span>
                <Pick
                  value={draft?.laborManualUnit || "$"}
                  options={EXPENSE_UNITS}
                  onChange={(v) => set({ laborManualUnit: v })}
                />
              </div>
            </div>
            <p className="gmbw-hint">Wages for the period.</p>
          </>
        )}
      </div>

      {/* ── Expenses ──────────────────────────────────────────────────── */}
      <div className="gmbw-fld">
        <label>
          Expenses
          <InfoTip text="Nothing in the app knows what rent costs, so these are typed — one line per cost, each a dollar figure or a share of revenue." />
        </label>
        {items.map((item, i) => (
          <div className="gmbw-mrow" key={item.key || `exp${i}`}>
            <div className="gmbw-mrow-top">
              <div className="gmbw-lbr-pick">
                <input
                  type="text"
                  className="gmbw-title-input"
                  value={item.label || ""}
                  placeholder="e.g. Rent, Fuel, Insurance, Software"
                  maxLength={40}
                  aria-label="What this cost is"
                  onChange={(e) => patchItem(i, { label: e.target.value })}
                />
              </div>
              <button
                type="button"
                className="gmbw-mrow-x"
                aria-label="Remove this expense"
                onClick={() => setItems(items.filter((_, ix) => ix !== i))}
              >
                {IcX}
              </button>
            </div>
            <div className="gmbw-lbr-grid" style={{ marginTop: 8 }}>
              <Num
                label="Amount"
                value={item.amount}
                prefix={item.unit === "%" ? null : "$"}
                suffix={item.unit === "%" ? "%" : null}
                onChange={(v) => patchItem(i, { amount: v })}
              />
              <div>
                <span className="gmbw-lbr-numlabel">Entered as</span>
                <Pick
                  value={item.unit || "$"}
                  options={EXPENSE_UNITS}
                  onChange={(v) => patchItem(i, { unit: v })}
                />
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="gmbw-addrow" onClick={() => setItems([...items, emptyExpense("opex")])}>
          + Add expense
        </button>
        <p className="gmbw-hint">
          <b>$ for the period</b> is one figure for whatever range the card is on and draws as a flat line —
          the usual case. <b>$ per day</b> rescales itself when the range changes. <b>% of revenue</b> follows
          revenue up and down instead of running flat.
        </p>
      </div>

      {/* ── Goals ─────────────────────────────────────────────────────── */}
      <div className="gmbw-fld">
        <label>
          Goals and limits
          <InfoTip text="A ceiling for the costs, a floor for revenue and profit. Cross the warning and the tile turns amber; cross the limit and it turns red, with a dashed line on the chart showing where it sits." />
        </label>
        {SERIES_KEYS.map((key) => {
          const goal = goalOf(draft, key);
          return (
            <div className="gmbw-mrow" key={key}>
              <Switch
                on={!!goal.on}
                label={SERIES_LABEL[key]}
                note={goal.on ? undefined : "No goal — this tile just reports the number."}
                onToggle={() => setGoal(key, { on: !goal.on })}
              />
              {goal.on && (
                <div className="gmbw-lbr-grid" style={{ marginTop: 4 }}>
                  <div>
                    <span className="gmbw-lbr-numlabel">Should be</span>
                    <Pick
                      value={goal.dir}
                      options={[
                        { value: "max", label: "No more than" },
                        { value: "min", label: "At least" },
                      ]}
                      onChange={(v) => setGoal(key, { dir: v })}
                    />
                  </div>
                  <div>
                    <span className="gmbw-lbr-numlabel">Measured in</span>
                    <Pick
                      value={goal.unit}
                      options={[
                        { value: "%", label: "% of revenue" },
                        { value: "$", label: "Dollars" },
                      ]}
                      onChange={(v) => setGoal(key, { unit: v })}
                    />
                  </div>
                  <Num
                    label="Warn at"
                    value={goal.warn}
                    prefix={goal.unit === "$" ? "$" : null}
                    suffix={goal.unit === "%" ? "%" : null}
                    placeholder="optional"
                    onChange={(v) => setGoal(key, { warn: v })}
                  />
                  <Num
                    label="Limit"
                    value={goal.limit}
                    prefix={goal.unit === "$" ? "$" : null}
                    suffix={goal.unit === "%" ? "%" : null}
                    onChange={(v) => setGoal(key, { limit: v })}
                  />
                  <p className="gmbw-hint" style={{ margin: "-4px 0 0", gridColumn: "1 / -1" }}>
                    {goal.dir === "max"
                      ? `${SERIES_LABEL[key]} goes red at the limit and above.`
                      : `${SERIES_LABEL[key]} goes red at the limit and below.`}
                    {" "}Leave <b>Warn at</b> empty to go straight from good to red.
                    {/* The one goal that does a second job — said here, where
                        it is typed, rather than only in the Chart block. */}
                    {key === "revenue" && goal.unit === "$" && (
                      <>
                        {" "}It also sets the <b>Pace line</b>: this figure ÷ the period&apos;s working
                        days is what a day has to bring.
                      </>
                    )}
                    {key === "revenue" && goal.unit === "%" && (
                      <>
                        {" "}A revenue goal in <b>%</b> is revenue measured against itself, so it draws no
                        Pace line — switch it to <b>$</b> for that.
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── The chart ─────────────────────────────────────────────────── */}
      <div className="gmbw-fld">
        <label>
          Chart
          <InfoTip text="Dollars keeps the three lines separated the way the sketch draws them. Percent puts every line on a share-of-revenue axis, which is where a “stay under 40%” limit can actually be drawn across the plot." />
        </label>
        <div className="gmbw-lbr-grid">
          <div>
            <span className="gmbw-lbr-numlabel">Y axis</span>
            <Pick
              value={draft?.unit === "%" ? "%" : "$"}
              options={[
                { value: "$", label: "Dollars" },
                { value: "%", label: "% of revenue" },
              ]}
              onChange={(v) => set({ unit: v })}
            />
          </div>
          <div>
            <span className="gmbw-lbr-numlabel">One point per</span>
            <Pick
              value={draft?.grain || "auto"}
              options={GRAINS.map((g) => ({ value: g.id, label: g.label }))}
              onChange={(v) => set({ grain: v })}
            />
          </div>
          <div>
            <span className="gmbw-lbr-numlabel">Weekends</span>
            <Pick
              value={draft?.workingDaysOnly === true ? "working" : "all"}
              options={[
                { value: "all", label: "Show every date" },
                { value: "working", label: "Working days only" },
              ]}
              onChange={(v) => set({ workingDaysOnly: v === "working" })}
            />
          </div>
          <div>
            <span className="gmbw-lbr-numlabel">Line shape</span>
            <Pick
              value={draft?.curve === "linear" ? "linear" : "monotone"}
              options={[
                { value: "monotone", label: "Smooth" },
                { value: "linear", label: "Straight" },
              ]}
              onChange={(v) => set({ curve: v })}
            />
          </div>
          <div>
            <span className="gmbw-lbr-numlabel">{labelOf("labor")} starts at</span>
            <Pick
              value={laborBase}
              options={[
                { value: "expenses", label: `On the ${labelOf("expenses")} line` },
                { value: "zero", label: "At zero" },
              ]}
              onChange={(v) => set({ laborBase: v })}
            />
          </div>
        </div>
        <p className="gmbw-hint">
          <b>Automatic</b> widens as the range does — a point per day up to a month, per week up to four, per
          month beyond that.
          {draft?.unit === "%" && " On a percent axis Revenue would be a flat 100% line, so Net profit is drawn in its place."}
        </p>
        <p className="gmbw-hint">
          <b>Working days only</b> folds each Saturday, Sunday and company holiday into the trading day
          beside it, so the axis only shows the days the company planned to trade — nothing earned or spent
          on a folded-in day is lost, it just draws on the point next to it instead of its own. Only changes
          anything when points are drawn per day (<b>Automatic</b> on a month-or-shorter range, or <b>Daily</b>).
        </p>
        <p className="gmbw-hint">
          <b>Pace line.</b> With a Revenue goal set in dollars, the card divides it by the working days in
          the period — Monday to Friday, minus the holidays on the company calendar — and draws what a day
          has to bring. On <b>this month</b> the chart runs to the last of the month, so the pace line keeps
          going after today and you can see how many days are left to make it up. Revenue that lands on a
          weekend counts toward the month; it just puts you ahead of the line rather than changing it.
        </p>
        <p className="gmbw-hint">
          {laborBase === "expenses" ? (
            <>
              <b>{labelOf("labor")} starts at the {labelOf("expenses").toLowerCase()} line</b> — it is drawn as a
              band sitting on it, not up from the axis, so it rises and falls with it and never crosses it. What is
              left between the top of that band and {labelOf("revenue").toLowerCase()} is the profit.
            </>
          ) : (
            <>
              <b>{labelOf("labor")} starts at zero</b> — every line is measured from the axis, so
              {" "}{labelOf("labor").toLowerCase()} and {labelOf("expenses").toLowerCase()} can cross each other.
            </>
          )}
        </p>
      </div>

      <div className="gmbw-fld">
        <label>Lines on the chart</label>
        <div className="gmbw-toggles">
          {["revenue", "expenses", "labor", "net"].map((id) => (
            <Switch
              key={id}
              on={lines.includes(id)}
              label={(draft?.labels || {})[id] || SERIES_LABEL[id]}
              onToggle={() => toggleLine(id)}
            />
          ))}
        </div>
        <p className="gmbw-hint">
          Which series are plotted. Their tiles are separate — switch a tile off under <b>Appearance</b>, and
          rename any of them there too.
        </p>
      </div>
    </>
  );
};

ProfitTrendSetup.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  /** The board's other cards, so Labor can name a Payroll vs Revenue one. */
  // eslint-disable-next-line react/forbid-prop-types
  boardWidgets: PropTypes.array,
};
ProfitTrendSetup.defaultProps = { boardWidgets: [] };

export default ProfitTrendSetup;

/**
 * True once the card has enough to draw something real.
 *
 * "Auto" revenue counts as set even with nothing stored — the card finds the
 * Total Revenue metric itself, and the empty state on the card is what says so
 * when there isn't one. Same for labor read from a single payroll card on the
 * board: nothing is saved until there are two to choose between.
 */
export const profitTrendConfigured = (opts) => {
  const mode = revenueModeOf(opts);
  let hasRevenue = true;
  if (mode === "manual") hasRevenue = revenueOf(opts) > 0;
  else if (mode === "pick") hasRevenue = !!(opts?.revenueWorkspaceId && opts?.revenueKpiId);
  const hasLabor = laborModeOf(opts) === "manual" ? Number(opts?.laborManual) > 0 : true;
  return hasRevenue && hasLabor;
};

/* Every formula the card runs, in the order data.js derives them. */
const FORMULAS = [
  { out: "Labor", terms: ["Wages"] },
  { out: "Net profit", terms: ["Revenue", "−", "Labor", "−", "Expenses"] },
  { out: "Labor %", terms: ["Labor", "÷", "Revenue"] },
  { out: "Expense %", terms: ["Expenses", "÷", "Revenue"] },
  { out: "Net profit %", terms: ["Net profit", "÷", "Revenue"] },
];

/* Not a figure the card reports — where the Labor LINE is drawn, the one
   number on the chart that nothing else on the card states. Only listed when
   the card is actually drawing it that way. */
const LABOR_LINE_FORMULA = { out: "Labor line sits at", terms: ["Expenses", "+", "Labor"] };

/* Shown whenever a Revenue goal in dollars is set - the figure the pace line
   is drawn at, which is the one number on the chart nothing else states. */
const PACE_FORMULA = { out: "Daily pace", terms: ["Revenue goal", "÷", "Working days"] };

const IcSigma = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 5H7l6 7-6 7h11" />
  </svg>
);

export const ProfitFormulaCard = ({ draft }) => {
  const stacked = laborBaseOf(draft) === "expenses";
  const revenueGoal = goalOf(draft, "revenue");
  const paced = !!revenueGoal.on && revenueGoal.unit === "$" && revenueGoal.limit !== "" && revenueGoal.limit != null;
  const rows = [
    ...FORMULAS,
    ...(stacked ? [LABOR_LINE_FORMULA] : []),
    ...(paced ? [PACE_FORMULA] : []),
  ];
  return (
    <div className="gmbw-formula">
      <div className="gmbw-formula-h">
        <span className="gmbw-formula-ic">{IcSigma}</span>
        How every figure is calculated
      </div>
      <dl className="gmbw-formula-list">
        {rows.map((f) => (
          <div className="gmbw-formula-row" key={f.out}>
            <dt>{f.out}</dt>
            <dd>
              {f.terms.map((t, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={i} className={/^[÷×−+]$/.test(t) ? "op" : "term"}>{t}</span>
              ))}
            </dd>
          </div>
        ))}
      </dl>
      <p className="gmbw-formula-note">
        <b>Wages</b> come from the Payroll vs Revenue card this one reads (its own hours × pay/hour), re-summed
        for this card&apos;s date range — or a typed figure, if this card isn&apos;t pointed at one.
        <b> Revenue</b> syncs from a metric or is typed. <b>Expenses</b> are always typed, one line per cost.
        {stacked && (
          <>
            <b> Labor</b> is drawn from the expenses line rather than from zero, so the chart shows what it cost
            on top of everything else — change that under <b>Chart</b>.
          </>
        )}
        {paced && (
          <>
            <b> Working days</b> are Monday to Friday minus the company calendar&apos;s holidays — the same count
            the calendar itself shows.
          </>
        )}
      </p>
    </div>
  );
};
ProfitFormulaCard.propTypes = {
  /* The card's own settings, only so the list can say where the Labor line is
     drawn. Omitted on a preview, which falls to the default baseline. */
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object,
};
ProfitFormulaCard.defaultProps = { draft: null };
